import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  backfillSeries,
  fetchConsoleSeries,
  fetchConsoleSources,
  fetchIngestionLog,
  fetchRawBars,
  fetchRawQuarantine,
  getConsoleJob,
  refetchSeries,
  runSourceNow,
  type ConsoleJobDto,
  type IngestionLogRowDto,
  type QuarantineRowDto,
  type RawBarRowDto,
  type SeriesStatusDto,
  type SourceStatusDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

const STATUS_RANK: Record<string, number> = { FAILED: 0, "BACKFILL-GAP": 1, STALE: 2, FRESH: 3 };

function StatusChip({ status }: { status: string | null }) {
  const s = status ?? "UNKNOWN";
  const cls = s === "FRESH" ? "st-champion" : s === "FAILED" ? "st-challenger" : "chip";
  return <span className={`chip ${cls}`} style={s === "BACKFILL-GAP" ? { color: "var(--accent)" } : undefined}>{s}</span>;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DataConsolePage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [sources, setSources] = useState<SourceStatusDto[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [series, setSeries] = useState<SeriesStatusDto[] | null>(null);
  const [log, setLog] = useState<IngestionLogRowDto[] | null>(null);
  const [rawSeries, setRawSeries] = useState<string | null>(null);
  const [rawBars, setRawBars] = useState<RawBarRowDto[] | null>(null);
  const [rawQuarantine, setRawQuarantine] = useState<QuarantineRowDto[] | null>(null);
  const [rawTab, setRawTab] = useState<"stored" | "quarantine">("stored");
  const [error, setError] = useState<string | null>(null);

  const [activeJob, setActiveJob] = useState<ConsoleJobDto | null>(null);
  const [backfillFor, setBackfillFor] = useState<string | null>(null);
  const [backfillStart, setBackfillStart] = useState(todayIso());
  const [backfillEnd, setBackfillEnd] = useState(todayIso());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  function refreshCurrentSource() {
    if (!selected) return;
    Promise.all([fetchConsoleSources(token), fetchConsoleSeries(token, selected, 50), fetchIngestionLog(token, selected, 15)])
      .then(([src, s, l]) => {
        setSources(src);
        setSeries(s);
        setLog(l);
      })
      .catch(() => {
        // a refresh failing silently is fine -- the job panel already shows the real result
      });
  }

  function watchJob(job: ConsoleJobDto) {
    setActiveJob(job);
    if (pollRef.current) clearInterval(pollRef.current);
    if (job.status !== "running") {
      refreshCurrentSource();
      return;
    }
    pollRef.current = setInterval(() => {
      getConsoleJob(token, job.job_id)
        .then((updated) => {
          setActiveJob(updated);
          if (updated.status !== "running") {
            if (pollRef.current) clearInterval(pollRef.current);
            refreshCurrentSource();
          }
        })
        .catch((err) => {
          if (pollRef.current) clearInterval(pollRef.current);
          if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "job poll failed");
        });
    }, 2000);
  }

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function triggerRunNow(source: string) {
    runSourceNow(token, source)
      .then(watchJob)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "run-now failed");
      });
  }

  function triggerRefetch(source: string, seriesKey: string) {
    refetchSeries(token, source, seriesKey)
      .then(watchJob)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "re-fetch failed");
      });
  }

  function triggerBackfill(source: string, seriesKey: string) {
    backfillSeries(token, source, backfillStart, backfillEnd, [seriesKey])
      .then((job) => {
        setBackfillFor(null);
        watchJob(job);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "backfill failed");
      });
  }

  useEffect(() => {
    fetchConsoleSources(token)
      .then((rows) => {
        setSources(rows);
        if (rows.length && !selected) setSelected(rows[0].source);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selected) return;
    setRawSeries(null);
    setRawBars(null);
    setRawQuarantine(null);
    Promise.all([fetchConsoleSeries(token, selected, 50), fetchIngestionLog(token, selected, 15)])
      .then(([s, l]) => {
        setSeries(s);
        setLog(l);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selected]);

  function openRaw(seriesKey: string) {
    setRawSeries(seriesKey);
    setRawTab("stored");
    Promise.all([fetchRawBars(token, seriesKey, 100), fetchRawQuarantine(token, seriesKey, 100)])
      .then(([bars, quar]) => {
        setRawBars(bars);
        setRawQuarantine(quar);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  const rankedSeries = useMemo(() => {
    if (!series) return [];
    return [...series].sort((a, b) => (STATUS_RANK[a.status ?? ""] ?? 4) - (STATUS_RANK[b.status ?? ""] ?? 4));
  }, [series]);

  const { sorted: sortedSeries, sortKey: seriesSortKey, sortDir: seriesSortDir, toggleSort: toggleSeriesSort } = useSort<SeriesStatusDto>(
    rankedSeries,
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  function exportRawCsv() {
    if (!rawSeries) return;
    const rows = rawTab === "stored" ? rawBars ?? [] : rawQuarantine ?? [];
    if (!rows.length) return;
    const cols = Object.keys(rows[0]).map((c) => ({ key: c, label: c }));
    exportRowsAsCsv(rows as unknown as Record<string, unknown>[], cols, `${rawTab}_${rawSeries}.csv`);
  }

  function exportSourcesCsv() {
    if (!sources) return;
    exportRowsAsCsv(
      sources as unknown as Record<string, unknown>[],
      [
        { key: "source", label: "Source" },
        { key: "status", label: "Status" },
        { key: "last_good_date", label: "Last good date" },
        { key: "gap_count", label: "Gaps" },
      ],
      "sources.csv",
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Data console</h1>
          <div className="page-sub">Spot a problem, verify the raw rows — read-only this build; fixes need the write-path below.</div>
        </div>
      </div>

      <PageHelper>
        Verify data health source-by-source: freshness <FieldHelp term="freshness_status" />status, per-series row
        counts and gaps, recent ingestion runs, and raw stored-vs-<FieldHelp term="quarantine" />quarantined rows.
        Run now / Re-fetch / Backfill trigger a REAL ingestion run against the live data sources (Breeze, yfinance,
        NSE Bhavcopy) — these write real bars, so use them deliberately, not to "see what happens."
      </PageHelper>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          <b>Run now / Re-fetch / Backfill trigger a real, asynchronous ingestion job</b> against the live source
          (the same functions the daily cron calls) — because a single fetch can take several seconds and a
          full-source run-now loops that across the whole active universe, each action returns a job immediately
          and the panel below polls it to completion. Upserts on (security, date) make re-running the same window
          safe — it refreshes, never duplicates.
        </span>
      </div>

      {activeJob && (
        <div className="card" style={{ marginBottom: 16, borderColor: activeJob.status === "error" ? "var(--bad)" : undefined }}>
          <div className="h3">
            <span>
              Job — {activeJob.kind} {activeJob.status === "running" && <span className="chip">running…</span>}
              {activeJob.status === "done" && <span className="chip st-champion">done</span>}
              {activeJob.status === "error" && <span className="chip st-challenger">error</span>}
            </span>
            <button className="btn ghost sm" onClick={() => { setActiveJob(null); if (pollRef.current) clearInterval(pollRef.current); }}>✕ dismiss</button>
          </div>
          <div className="mono" style={{ fontSize: "0.76rem", color: "var(--ink-faint)", marginBottom: 6 }}>
            {JSON.stringify(activeJob.params)} · triggered by {activeJob.triggered_by}
          </div>
          {activeJob.error && <div className="error" style={{ fontSize: "0.8rem" }}>{activeJob.error}</div>}
          {activeJob.status === "done" && Array.isArray(activeJob.result) && (
            <div className="table-wrap" style={{ maxHeight: 220, overflow: "auto" }}>
              <table className="stocks-table">
                <thead><tr><th className="l">Security</th><th>Status</th><th className="r">Fetched</th><th className="r">Promoted</th><th className="r">Quarantined</th></tr></thead>
                <tbody>
                  {(activeJob.result as { security_id: string; status: string; fetched: number; promoted: number; quarantined: number; error: string | null }[]).map((r) => (
                    <tr key={r.security_id}>
                      <td className="l mono" style={{ fontSize: "0.78rem" }}>{r.security_id}</td>
                      <td><StatusChip status={r.status} /></td>
                      <td className="r mono">{r.fetched}</td>
                      <td className="r mono">{r.promoted}</td>
                      <td className="r mono">{r.quarantined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 0, marginBottom: 16, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 12px" }}>
          <button className="btn ghost sm" onClick={exportSourcesCsv}>↓ CSV</button>
        </div>
        <table className="stocks-table">
          <thead>
            <tr>
              <th className="l">Source</th>
              <th>Status <FieldHelp term="freshness_status" /></th>
              <th>Cadence <FieldHelp term="source_cadence" /></th>
              <th>Last good date</th>
              <th className="r">Gaps / rows</th>
              <th>Last run</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(sources ?? []).map((s) => (
              <tr
                key={s.source}
                className={s.extended ? undefined : "clickable"}
                style={{ cursor: s.extended ? "default" : "pointer", background: selected === s.source ? "var(--surface-2)" : undefined }}
                onClick={() => !s.extended && setSelected(s.source)}
              >
                <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>
                  {s.label ?? s.source}
                </td>
                <td><StatusChip status={s.status} /></td>
                <td className="mono" style={{ fontSize: "0.78rem" }}>{s.cadence ?? "daily"}</td>
                <td className="mono" style={{ fontSize: "0.8rem" }}>
                  {s.last_good_date ?? "—"}
                  {s.days_since_last_data != null && s.days_since_last_data >= 0 && (
                    <span className="mut" style={{ marginLeft: 6, fontSize: "0.72rem" }}>
                      ({s.days_since_last_data}d ago)
                    </span>
                  )}
                </td>
                <td className="r mono">
                  {s.extended
                    ? (s.row_count?.toLocaleString() ?? "—")
                    : (s.gap_count ?? 0)}
                </td>
                <td className="mono" style={{ fontSize: "0.72rem" }}>
                  {s.last_run_at ? (
                    <span title={s.last_run_status ?? undefined}>
                      {s.last_run_at.slice(0, 16).replace("T", " ")}
                    </span>
                  ) : (
                    <span className="mut">no run history logged</span>
                  )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn ghost sm"
                    disabled={activeJob?.status === "running"}
                    title={s.run_now_note ?? `Run the ${s.source} incremental fetch now, for its whole active universe`}
                    onClick={() => triggerRunNow(s.source)}
                  >
                    ▶ Run now
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16 }}>
          <div className="card">
            <div className="h3"><span>Per-series freshness — {selected} <span className="note">worst first</span></span></div>
            <div className="table-wrap" style={{ maxHeight: 420, overflow: "auto" }}>
              <table className="stocks-table">
                <thead>
                  <tr>
                    <th className="l clickable" onClick={() => toggleSeriesSort("series_key")}>Series {sortGlyph("series_key", seriesSortKey, seriesSortDir)}</th>
                    <th className="clickable" onClick={() => toggleSeriesSort("status")}>Status <FieldHelp term="freshness_status" /> {sortGlyph("status", seriesSortKey, seriesSortDir)}</th>
                    <th className="r clickable" onClick={() => toggleSeriesSort("row_count")}>Rows {sortGlyph("row_count", seriesSortKey, seriesSortDir)}</th>
                    <th className="r clickable" onClick={() => toggleSeriesSort("gap_count")}>Gaps {sortGlyph("gap_count", seriesSortKey, seriesSortDir)}</th>
                    <th>Coverage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSeries.map((s) => (
                    <tr key={s.series_key}>
                      <td className="l">
                        <div style={{ fontWeight: 600 }}>{s.display_ticker ?? s.series_key}</div>
                        {s.display_ticker && <div className="mut mono" style={{ fontSize: "0.72rem" }}>{s.series_key}</div>}
                      </td>
                      <td><StatusChip status={s.status} /></td>
                      <td className="r mono">{s.row_count?.toLocaleString() ?? "—"}</td>
                      <td className="r mono">{s.gap_count ?? 0}</td>
                      <td className="mono" style={{ fontSize: "0.74rem", color: "var(--ink-faint)" }}>{s.coverage_start ?? "—"} → {s.coverage_end ?? "—"}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                          {(s.status === "STALE" || s.status === "FAILED") && (
                            <button
                              className="btn ghost sm" disabled={activeJob?.status === "running"}
                              title={`Re-fetch just ${s.series_key} now`}
                              onClick={() => selected && triggerRefetch(selected, s.series_key)}
                            >
                              ⟳ Re-fetch
                            </button>
                          )}
                          {s.status === "BACKFILL-GAP" && backfillFor !== s.series_key && (
                            <button
                              className="btn ghost sm" disabled={activeJob?.status === "running"}
                              title="Targeted gap backfill over an explicit date range"
                              onClick={() => { setBackfillFor(s.series_key); setBackfillStart(s.coverage_end ?? todayIso()); setBackfillEnd(todayIso()); }}
                            >
                              ⬒ Backfill…
                            </button>
                          )}
                          {backfillFor === s.series_key && (
                            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <input type="date" value={backfillStart} onChange={(e) => setBackfillStart(e.target.value)} style={{ fontSize: "0.72rem", padding: "2px 4px" }} />
                              <span className="mut">→</span>
                              <input type="date" value={backfillEnd} onChange={(e) => setBackfillEnd(e.target.value)} style={{ fontSize: "0.72rem", padding: "2px 4px" }} />
                              <button className="btn ghost sm" onClick={() => selected && triggerBackfill(selected, s.series_key)}>Go</button>
                              <button className="btn ghost sm" onClick={() => setBackfillFor(null)}>✕</button>
                            </span>
                          )}
                          <button className="btn ghost sm" onClick={() => openRaw(s.series_key)}>⛁ View raw</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {series && series.length === 0 && (
                    <tr><td colSpan={6} className="mut" style={{ textAlign: "center", padding: 20 }}>No per-series rows for this source yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="h3"><span>Recent runs — {selected}</span></div>
            <table className="stocks-table">
              <thead>
                <tr><th>Run at</th><th className="r">Attempted</th><th className="r">Inserted</th><th className="r">Errors</th></tr>
              </thead>
              <tbody>
                {(log ?? []).map((r) => (
                  <tr key={r.run_id}>
                    <td className="mono" style={{ fontSize: "0.76rem" }}>{r.run_at.slice(0, 16).replace("T", " ")}</td>
                    <td className="r mono">{r.rows_attempted?.toLocaleString() ?? "—"}</td>
                    <td className="r mono">{r.rows_inserted?.toLocaleString() ?? "—"}</td>
                    <td className="r mono" style={{ color: r.error_count > 0 ? "var(--bad)" : undefined }}>{r.error_count}</td>
                  </tr>
                ))}
                {log && log.length === 0 && (
                  <tr><td colSpan={4} className="mut" style={{ textAlign: "center", padding: 20 }}>No ingestion_log rows for this source.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rawSeries && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="h3">
            <span>Raw inspection — {rawSeries}</span>
            <button className="btn ghost sm" onClick={exportRawCsv}>↓ CSV</button>
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
            <button className={`app-nav-tab${rawTab === "stored" ? " active" : ""}`} style={{ padding: "5px 10px", fontSize: "0.75rem" }} onClick={() => setRawTab("stored")}>
              Stored rows ({rawBars?.length ?? 0})
            </button>
            <button className={`app-nav-tab${rawTab === "quarantine" ? " active" : ""}`} style={{ padding: "5px 10px", fontSize: "0.75rem" }} onClick={() => setRawTab("quarantine")}>
              Quarantine ({rawQuarantine?.length ?? 0})
            </button>
            <FieldHelp term="quarantine" />
          </div>
          {rawTab === "stored" ? (
            <div className="table-wrap" style={{ maxHeight: 300, overflow: "auto" }}>
              <table className="stocks-table">
                <thead><tr><th>Date</th><th className="r">Open</th><th className="r">High</th><th className="r">Low</th><th className="r">Close</th><th className="r">Volume</th><th>Source</th></tr></thead>
                <tbody>
                  {(rawBars ?? []).map((r, i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize: "0.78rem" }}>{r.date}</td>
                      <td className="r mono">{Number(r.open).toFixed(2)}</td>
                      <td className="r mono">{Number(r.high).toFixed(2)}</td>
                      <td className="r mono">{Number(r.low).toFixed(2)}</td>
                      <td className="r mono">{Number(r.close).toFixed(2)}</td>
                      <td className="r mono">{r.volume.toLocaleString()}</td>
                      <td className="mono" style={{ fontSize: "0.76rem", color: "var(--ink-faint)" }}>{r.source}</td>
                    </tr>
                  ))}
                  {rawBars && rawBars.length === 0 && <tr><td colSpan={7} className="mut" style={{ textAlign: "center", padding: 20 }}>No stored bars for this series.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-wrap" style={{ maxHeight: 300, overflow: "auto" }}>
              <table className="stocks-table">
                <thead><tr><th>Date</th><th>Reason</th><th>Check failed</th></tr></thead>
                <tbody>
                  {(rawQuarantine ?? []).map((r, i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize: "0.78rem" }}>{r.date}</td>
                      <td>{r.reason}</td>
                      <td style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>
                        {r.failed_checks.map((c, j) => <div key={j}><span className="chip">{c.check}</span> {c.detail}</div>)}
                      </td>
                    </tr>
                  ))}
                  {rawQuarantine && rawQuarantine.length === 0 && <tr><td colSpan={3} className="mut" style={{ textAlign: "center", padding: 20 }}>Nothing quarantined for this series — honest, not fabricated.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
